import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Sheet, SheetContent, SheetTitle, SheetHeader, SheetTrigger } from '../ui/sheet'
import { useAssetTypes, withoutArchivedTypes } from '../../hooks/useQueries'
import { useIsMobile } from '../../hooks/use-mobile'
import { SPEC_TRADE_CODES } from '../../lib/equipment/tradeOrder'
import { AssetTypeTile } from '../assets/AssetTypeTile'

/**
 * Picker for equipment asset types, grouped by trade.
 *
 * Used at two entry points (per spec 2026-05-05-01):
 *   1. The `+` button on each trade row of the equipment tab — opens with
 *      the clicked trade's group anchored at the top.
 *   2. The asset-type field on `/createBasic` when the parent's allowed
 *      children include any equipment-typed asset type.
 *
 * The component is the menu surface only; the trigger and post-pick flow
 * live in the wrapper.
 */
export interface AssetTypeLite {
  uid: string
  code: string
  name?: unknown
  emoji?: string | null
  /** MDI icon name — what AssetTypeTile draws (spec 2026-09-17-04). */
  icon?: string | null
  kind?: string
  inherits_from_asset_type_uid?: string | null
  equipment_properties?: { multiple?: boolean | null } | null
  /** The type's free-form bag — carries `plan_symbol` (spec 2026-08-22-01 §5). */
  properties?: Record<string, unknown> | null
}

export interface EquipmentTypePickerProps {
  org: string
  /**
   * When set, opens the picker with this trade's group anchored at top
   * and shown as a dismissible chip ("HVAC ✕"). Other trades remain
   * visible below for cross-trade discovery.
   */
  defaultTradeUid?: string
  /** Called when the user clicks the dismissible chip. The wrapper should
   *  clear `defaultTradeUid` so the picker re-orders to default. */
  onClearDefaultTrade?: () => void
  /** Controlled-open API. The component does not own the trigger button. */
  open: boolean
  onOpenChange: (next: boolean) => void
  onPick: (assetType: AssetTypeLite) => void
  /** Trigger element rendered by the wrapper (used as Popover anchor). */
  children: React.ReactNode
  /** Optional aria-label for the search input + sheet title. */
  ariaLabel?: string
  /** When true, render a multi-select chip row above the search input so
   *  the user can pre-filter to one or several trades. Used by the
   *  createBasic entry point per spec §4.2-B. */
  showTradeChipFilters?: boolean
  /**
   * Replaces the type tile in front of each row. The plan editor passes the
   * asset type's `plan_symbol` rendered as inline SVG, so what the user picks
   * is what lands on the drawing (spec 2026-08-22-02 §5).
   */
  renderItemPrefix?: (item: AssetTypeLite) => React.ReactNode
}

function localizedLabel(at: AssetTypeLite, lang: string): string {
  if (typeof at.name === 'string') return at.name
  if (at.name && typeof at.name === 'object') {
    const m = at.name as Record<string, string>
    return m[lang] || m['en'] || m['fr'] || at.code
  }
  return at.code
}

function isTrade(at: AssetTypeLite): boolean {
  if (at.kind === 'trade') return true
  if (at.kind) return false
  return typeof at.code === 'string' && at.code.startsWith('trade_')
}

function isEquipment(at: AssetTypeLite): boolean {
  if (at.kind === 'equipment') return true
  return false
}

export function EquipmentTypePicker(props: EquipmentTypePickerProps) {
  const { org, defaultTradeUid, onClearDefaultTrade, open, onOpenChange, onPick, children, ariaLabel, showTradeChipFilters, renderItemPrefix } = props
  const { t, i18n } = useTranslation('assets')
  const isMobile = useIsMobile()
  const { data: allAssetTypesRaw = [] } = useAssetTypes(org)
  const allAssetTypes = useMemo(() => withoutArchivedTypes(allAssetTypesRaw), [allAssetTypesRaw])

  // Local override that lets the user dismiss the anchored trade chip even
  // when the parent doesn't supply an `onClearDefaultTrade` handler.
  const [localCleared, setLocalCleared] = useState(false)
  // Reset the override every time the picker is reopened or the parent
  // changes the anchored trade.
  useEffect(() => {
    if (!open) setLocalCleared(false)
  }, [open])
  useEffect(() => {
    setLocalCleared(false)
  }, [defaultTradeUid])
  const effectiveDefaultTradeUid = localCleared ? undefined : defaultTradeUid

  // Multi-select trade chip filters (createBasic entry point, §4.2-B).
  const [chipFilters, setChipFilters] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!open) setChipFilters(new Set())
  }, [open])
  const toggleChipFilter = (uid: string) => {
    setChipFilters((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })
  }

  const trades = useMemo<AssetTypeLite[]>(() => {
    const list = (allAssetTypes as unknown as AssetTypeLite[]).filter(isTrade)
    return list.sort((a, b) => {
      const ai = SPEC_TRADE_CODES.indexOf(a.code as never)
      const bi = SPEC_TRADE_CODES.indexOf(b.code as never)
      if (ai !== -1 && bi !== -1) return ai - bi
      if (ai !== -1) return -1
      if (bi !== -1) return 1
      return a.code.localeCompare(b.code)
    })
  }, [allAssetTypes])

  const equipmentByTrade = useMemo<Map<string, AssetTypeLite[]>>(() => {
    const m = new Map<string, AssetTypeLite[]>()
    for (const at of allAssetTypes as unknown as AssetTypeLite[]) {
      if (!isEquipment(at)) continue
      const parent = at.inherits_from_asset_type_uid
      if (!parent) continue
      const arr = m.get(parent) ?? []
      arr.push(at)
      m.set(parent, arr)
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => localizedLabel(a, i18n.language).localeCompare(localizedLabel(b, i18n.language)))
    }
    return m
  }, [allAssetTypes, i18n.language])

  const orderedTrades = useMemo<AssetTypeLite[]>(() => {
    let base = trades
    if (chipFilters.size > 0) {
      base = trades.filter((t) => chipFilters.has(t.uid))
    }
    if (!effectiveDefaultTradeUid) return base
    const idx = base.findIndex((t) => t.uid === effectiveDefaultTradeUid)
    if (idx <= 0) return base
    const head = base[idx]
    return [head, ...base.slice(0, idx), ...base.slice(idx + 1)]
  }, [trades, effectiveDefaultTradeUid, chipFilters])

  /**
   * One flat list, in trade order — no headings.
   *
   * The trades still decide the order, but they are no longer walls between
   * the rows: a picker opened to *search* reads as one list, and a heading
   * every three items is chrome between the query and its answer. Each row
   * carries its trade on the right instead, muted, where it names the thing
   * without interrupting the scan down the labels.
   */
  const rows = useMemo(
    () => orderedTrades.flatMap((trade) =>
      (equipmentByTrade.get(trade.uid) ?? []).map((item) => ({ item, trade }))),
    [orderedTrades, equipmentByTrade],
  )

  // The picker is opened to be typed in — by the plan editor the moment an
  // equipment is dropped with no type, and by every other entry point too.
  // Radix moves focus to the popover, not into the field, so the first
  // keystroke would otherwise go nowhere.
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!open) return
    const id = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [open])

  const renderContent = () => (
    <Command shouldFilter={true} className="rounded-md border bg-card">
      {effectiveDefaultTradeUid && (
        <div className="flex items-center gap-2 px-3 py-2 border-b" data-testid="equipment-picker-trade-chip-row">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5 hover:bg-primary/20"
            aria-pressed={true}
            onClick={() => {
              setLocalCleared(true)
              onClearDefaultTrade?.()
            }}
            data-testid="equipment-picker-clear-trade"
            title={t('equipment_picker.clear_trade')}
          >
            <span>{localizedLabel(trades.find((t) => t.uid === effectiveDefaultTradeUid) ?? { uid: '', code: '' }, i18n.language)}</span>
            <span aria-hidden>×</span>
          </button>
        </div>
      )}
      {showTradeChipFilters && !effectiveDefaultTradeUid && trades.length > 0 && (
        <div
          className="flex items-center gap-1.5 px-3 py-2 border-b flex-wrap"
          data-testid="equipment-picker-trade-filter-row"
        >
          {trades.map((trade) => {
            const active = chipFilters.has(trade.uid)
            return (
              <button
                key={trade.uid}
                type="button"
                role="button"
                aria-pressed={active}
                onClick={() => toggleChipFilter(trade.uid)}
                className={
                  'inline-flex items-center gap-1 rounded-full text-xs px-2 py-0.5 border transition-colors ' +
                  (active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-transparent text-muted-foreground border-border hover:bg-muted')
                }
                data-testid={`equipment-picker-trade-chip-${trade.code}`}
              >
                <AssetTypeTile type={trade} size="sm" className="!h-4 !w-4 !rounded" />
                <span>{localizedLabel(trade, i18n.language)}</span>
              </button>
            )
          })}
        </div>
      )}
      <CommandInput
        ref={inputRef}
        autoFocus
        placeholder={t('equipment_picker.search_placeholder')}
        aria-label={ariaLabel || t('equipment_picker.search_aria_label')}
        data-testid="equipment-picker-input"
      />
      <CommandList>
        <CommandEmpty>{t('equipment_picker.empty')}</CommandEmpty>
        <CommandGroup>
          {rows.map(({ item, trade }) => {
            const heading = localizedLabel(trade, i18n.language)
            const label = localizedLabel(item, i18n.language)
            const isBatch = item.equipment_properties?.multiple === true
            return (
              <CommandItem
                key={item.uid}
                // The trade stays in the searchable value: typing "hvac" still
                // finds a radiator now that the heading no longer says so.
                value={`${heading} ${label} ${item.code}`}
                onSelect={() => {
                  onPick(item)
                  onOpenChange(false)
                }}
                data-testid={`equipment-picker-item-${item.code}`}
              >
                <span className="flex items-center gap-2 flex-1 min-w-0">
                  {renderItemPrefix ? (
                    renderItemPrefix(item)
                  ) : (
                    <AssetTypeTile type={item} size="sm" />
                  )}
                  <span className="truncate">{label}</span>
                </span>
                {isBatch && (
                  <span className="text-xs text-muted-foreground" aria-label={t('equipment_picker.batch_hint')}>
                    {t('equipment_picker.batch_hint')}
                  </span>
                )}
                <span
                  className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground/70"
                  data-testid={`equipment-picker-item-trade-${item.code}`}
                >
                  {heading}
                </span>
              </CommandItem>
            )
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  )

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>{children}</SheetTrigger>
        <SheetContent side="bottom" className="h-[75vh] p-0" data-testid="equipment-picker-drawer">
          <SheetHeader className="px-3 py-2 border-b">
            <SheetTitle>{ariaLabel || t('equipment_picker.search_aria_label')}</SheetTitle>
          </SheetHeader>
          <div className="h-full overflow-y-auto">{renderContent()}</div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="end" data-testid="equipment-picker-popover">
        {renderContent()}
      </PopoverContent>
    </Popover>
  )
}
